import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API, BACKEND_URL } from '../../App';
import WagryCalendar from './WagryCalendar';
import AdvertPreviewDialog from './AdvertPreviewDialog';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  Loader2, Upload, Megaphone, Image as ImageIcon, Video, Music,
  Facebook, Instagram, Music2, Youtube, Send, RefreshCw, Copy, Trash2, CheckCircle,
  AlertTriangle, Clock, Edit, History, FileText, CalendarClock, XCircle, BookOpen,
  ListOrdered, Inbox, Eye, Info, ChevronsUpDown, Check,
} from 'lucide-react';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

const PLATFORMS = [
  { id: 'facebook', label: 'Facebook', icon: Facebook, color: 'text-blue-600' },
  { id: 'instagram', label: 'Instagram', icon: Instagram, color: 'text-pink-600' },
  { id: 'tiktok', label: 'TikTok', icon: Music2, color: 'text-slate-900' },
  { id: 'youtube', label: 'YouTube', icon: Youtube, color: 'text-red-600' },
];

const STATUS_BADGE = {
  draft: { label: 'Konsep', cls: 'bg-gray-200 text-gray-800' },
  queued: { label: 'In Wagry', cls: 'bg-amber-100 text-amber-800' },
  scheduled: { label: 'Geskeduleer', cls: 'bg-purple-100 text-purple-800' },
  published: { label: 'Gepubliseer', cls: 'bg-green-100 text-green-800' },
  partial: { label: 'Gedeeltelik', cls: 'bg-amber-100 text-amber-800' },
  pending: { label: 'Hangende', cls: 'bg-blue-100 text-blue-800' },
  failed: { label: 'Misluk', cls: 'bg-red-100 text-red-800' },
};

const RESULT_ICON = {
  success: <CheckCircle className="h-4 w-4 text-green-600" />,
  scheduled: <CalendarClock className="h-4 w-4 text-purple-600" />,
  failed: <AlertTriangle className="h-4 w-4 text-red-600" />,
  pending: <Clock className="h-4 w-4 text-blue-600" />,
};

const EMPTY_FORM = {
  id: null,
  title: '',
  caption: '',
  platforms: [],
  media_url: null,
  media_type: null,
  media_filename: null,
  size_bytes: 0,
};

const Adverteer = () => {
  const [tab, setTab] = useState('new');
  const [form, setForm] = useState(EMPTY_FORM);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [publishing, setPublishing] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduledAt, setScheduledAt] = useState(''); // datetime-local string
  const [scheduling, setScheduling] = useState(false);
  const [scheduled, setScheduled] = useState([]);
  const [cancellingSchedule, setCancellingSchedule] = useState(null);
  const [resultDialog, setResultDialog] = useState(null);
  const [limits, setLimits] = useState({ image_mb: 8, video_mb: 100, audio_mb: 25 });
  const [adverts, setAdverts] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [historyDialog, setHistoryDialog] = useState(null);
  const [publishedBooks, setPublishedBooks] = useState([]);
  const [linkedBookId, setLinkedBookId] = useState('');
  const [bookPickerOpen, setBookPickerOpen] = useState(false);
  const [queueDialogOpen, setQueueDialogOpen] = useState(false);
  const [queueTargetAt, setQueueTargetAt] = useState('');
  const [queueing, setQueueing] = useState(false);
  const [queueItems, setQueueItems] = useState([]);
  const [queueStatusData, setQueueStatusData] = useState(null);
  const [removingFromQueue, setRemovingFromQueue] = useState(null);
  const [queueView, setQueueView] = useState('calendar'); // 'calendar' | 'list'
  const [previewAdvert, setPreviewAdvert] = useState(null);
  const fileInputRef = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const pendingDeepLinkBookId = useRef(searchParams.get('book_id') || '');

  useEffect(() => {
    fetchLimits();
    fetchAdverts();
    fetchPublishedBooks();
    fetchQueue();
  }, []);

  const fetchLimits = async () => {
    try {
      const r = await axios.get(`${API}/adverts/limits`);
      setLimits(r.data);
    } catch (e) {
      console.debug('[adverts-limits] fetch failed:', e?.response?.status, e?.message);
    }
  };

  const fetchAdverts = async () => {
    setLoadingList(true);
    try {
      const r = await axios.get(`${API}/adverts`);
      const all = r.data || [];
      setAdverts(all);
      setDrafts(all.filter(a => a.status === 'draft'));
      setScheduled(all.filter(a => a.status === 'scheduled'));
    } catch (e) {
      toast.error('Kon nie advertensies laai nie.');
    } finally {
      setLoadingList(false);
    }
  };

  const fetchPublishedBooks = async () => {
    try {
      // Fetch all books and filter client-side — production data may have status fields
      // with odd casing/whitespace that would silently drop rows from a server-side filter.
      const r = await axios.get(`${API}/admin/books`);
      const all = r.data || [];
      const published = all.filter(b => {
        const s = (b.status || '').trim().toLowerCase();
        return s === 'gepubliseer';
      });
      const sorted = published.sort((a, b) => {
        // Alphabetical by book name — what the user actually scans by.
        // Was date-desc before; user got lost in hundreds of unsorted titles.
        return (a.boek_naam || '').localeCompare(b.boek_naam || '', 'af', { sensitivity: 'base' });
      });
      setPublishedBooks(sorted);
      // Handle deep-link from Boeke Bestuur ?book_id=...
      const wantId = pendingDeepLinkBookId.current;
      if (wantId) {
        const book = sorted.find(b => b.id === wantId);
        if (book) {
          applyBookLink(book);
          // strip the query param so refresh doesn't re-apply
          const next = new URLSearchParams(searchParams);
          next.delete('book_id');
          setSearchParams(next, { replace: true });
        }
        pendingDeepLinkBookId.current = '';
      }
    } catch (e) {
      console.error('fetchPublishedBooks failed:', e);
      toast.error('Kon nie gepubliseerde boeke laai vir Skakel met Boek nie.');
    }
  };

  const applyBookLink = (book) => {
    setLinkedBookId(book.id);
    const title = `Nuwe boek: ${book.boek_naam}`;
    const blurb = (book.blurb || '').trim();
    const author = book.skrywer_naam ? ` deur ${book.skrywer_naam}` : '';
    const caption = `${book.boek_naam}${author}\n\n${blurb}`.trim();
    const coverPath = book.cover_path || '';
    const coverUrl = coverPath.startsWith('http')
      ? coverPath
      : `${BACKEND_URL}/api/files/cover/${book.id}`;
    const safeName = (book.boek_naam || 'boek').replace(/[^a-z0-9]+/gi, '_').toLowerCase() + '_cover.jpg';
    setForm(prev => ({
      ...prev,
      title,
      caption,
      media_url: coverUrl,
      media_type: 'image',
      media_filename: safeName,
      size_bytes: 0,
    }));
    toast.success(`Voorblad en byskrif vir "${book.boek_naam}" gelaai.`);
  };

  const handleLinkBook = (bookId) => {
    setLinkedBookId(bookId);
    if (!bookId) return;
    const book = publishedBooks.find(b => b.id === bookId);
    if (!book) return;
    applyBookLink(book);
  };

  const fetchQueue = async (showErrors = false) => {
    try {
      const [s, l] = await Promise.all([
        axios.get(`${API}/adverts/queue/status`),
        axios.get(`${API}/adverts/queue/list`),
      ]);
      setQueueStatusData(s.data);
      setQueueItems(l.data || []);
      if (showErrors) toast.success('Wagry herlaai.');
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'onbekende fout';
      console.warn('[adverts-queue] fetch failed:', detail);
      // ALWAYS surface to the user when triggered via the Herlaai button — silent
      // failures here are how the production "Herlaai does nothing" bug went unnoticed.
      if (showErrors) toast.error(`Kon nie wagry laai nie: ${detail}`);
    }
  };

  const openQueueDialog = () => {
    if (!validateBeforePublish()) return;
    // Default suggestion: tomorrow at 09:00 local time
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(9, 0, 0, 0);
    const pad = (n) => String(n).padStart(2, '0');
    const defaultLocal = `${tomorrow.getFullYear()}-${pad(tomorrow.getMonth() + 1)}-${pad(tomorrow.getDate())}T${pad(tomorrow.getHours())}:${pad(tomorrow.getMinutes())}`;
    setQueueTargetAt(defaultLocal);
    setQueueDialogOpen(true);
  };

  const handleAddToQueue = async () => {
    if (!queueTargetAt) {
      toast.error('Kies asseblief ’n datum en tyd.');
      return;
    }
    const when = new Date(queueTargetAt);
    if (isNaN(when.getTime())) { toast.error('Ongeldige datum.'); return; }
    if (when.getTime() <= Date.now()) { toast.error('Doeldatum moet in die toekoms wees.'); return; }
    const targetIso = when.toISOString();
    setQueueDialogOpen(false);
    setQueueing(true);
    try {
      let advertId = form.id;
      if (!advertId) {
        const create = await axios.post(`${API}/adverts`, { ...form, status: 'draft' });
        advertId = create.data.advert.id;
      } else {
        await axios.put(`${API}/adverts/${advertId}`, {
          title: form.title, caption: form.caption, platforms: form.platforms,
          media_url: form.media_url, media_type: form.media_type,
          media_filename: form.media_filename, size_bytes: form.size_bytes,
        });
      }
      const r = await axios.post(`${API}/adverts/${advertId}/queue`, {
        scheduled_for: targetIso,
        platforms: form.platforms,
      });
      const adv = r.data.advert;
      const yt = r.data.youtube_result;
      let msg = `Advertensie in die wagry geplaas (${form.platforms.filter(p => p !== 'youtube').length} Buffer-platform(s)).`;
      if (yt) {
        if (yt.status === 'scheduled' || yt.status === 'success') msg += ' YouTube is dadelik opgelaai.';
        else msg += ` YouTube het misluk: ${yt.message || ''}`.trim();
      }
      toast.success(msg, { duration: 6000 });
      setForm(EMPTY_FORM);
      setLinkedBookId('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      setTab('queue');
      fetchAdverts();
      fetchQueue();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kon nie by wagry voeg nie.');
    } finally {
      setQueueing(false);
    }
  };

  const handleRemoveFromQueue = async (id) => {
    if (!window.confirm('Verwyder hierdie advertensie uit die wagry? Enige reeds-gestuurde Buffer plasings word gekanselleer en die advertensie keer terug na konsep.')) return;
    setRemovingFromQueue(id);
    try {
      const r = await axios.post(`${API}/adverts/${id}/remove-from-queue`);
      const failed = (r.data.failed_to_cancel || []).length;
      if (failed > 0) toast.warning(`Verwyder, maar ${failed} Buffer plasing(s) kon nie gekanselleer word nie.`);
      else toast.success('Uit wagry verwyder. Advertensie is nou ’n konsep.');
      fetchAdverts();
      fetchQueue();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kon nie verwyder nie.');
    } finally {
      setRemovingFromQueue(null);
    }
  };

  const handleFlushQueue = async () => {
    try {
      // 30s timeout so the user gets a fast error if the backend hangs — better
      // than silent stalls behind the proxy timeout.
      const r = await axios.post(`${API}/adverts/queue/flush-now`, null, { timeout: 30000 });
      const d = r.data || {};
      if (d.queued_count === 0 && d.failed_count === 0) {
        toast.info('Wagry is leeg — niks om te verwerk nie.');
      } else if (!d.buffer_configured) {
        toast.warning('Verwerking begin, maar Buffer is nie gekonfigureer nie — items sal misluk. Stel Buffer op in Admin Instellings.');
      } else if (d.in_horizon_count === 0 && d.queued_count > 0) {
        toast.info(`${d.queued_count} items in wagry, maar nie een is binne die ${d.horizon_hours || 48}u verwerk-horison nie.`);
      } else {
        toast.success(`Verwerking begin in agtergrond. ${d.in_horizon_count}/${d.queued_count} items binne horison.`);
      }
      // Refresh after a short delay so the background task has time to update DB
      setTimeout(() => { fetchQueue(false); fetchAdverts(); }, 2500);
    } catch (e) {
      if (e?.code === 'ECONNABORTED') {
        toast.error('Verwerking-aanvraag het uitgetel. Probeer "Diagnose" om te sien wat fout is.');
      } else {
        toast.error(e.response?.data?.detail || e?.message || 'Kon nie verwerk nie.');
      }
    }
  };

  const [topupBusy, setTopupBusy] = useState(false);
  const handleWeeklyTopup = async () => {
    setTopupBusy(true);
    try {
      const r = await axios.post(`${API}/adverts/queue/weekly-topup-now`, null, { timeout: 90000 });
      const d = r.data || {};
      if (d.moved > 0) {
        toast.success(`${d.moved} advertensie(s) na Geskeduleer geskuif. Geskeduleer is nou ${d.geskeduleer_count}.`);
      } else if (d.geskeduleer_count >= 8) {
        toast.info(`Geskeduleer is reeds vol (${d.geskeduleer_count}). Niks geskuif nie.`);
      } else {
        toast.info(`Niks om te skuif nie — Wagry is leeg. Geskeduleer: ${d.geskeduleer_count}.`);
      }
      if (d.notified) {
        toast.warning(`Wagry raak leeg (${d.wagry_remaining} oor) — ’n herinnering-e-pos is gestuur.`);
      }
      setTimeout(() => { fetchQueue(false); fetchAdverts(); }, 2500);
    } catch (e) {
      if (e?.response?.status === 403) {
        toast.error('Slegs die Hoof Admin kan die weeklikse aanvul hardloop.');
      } else {
        toast.error(e.response?.data?.detail || e?.message || 'Kon nie aanvul nie.');
      }
    } finally {
      setTopupBusy(false);
    }
  };

  const [diagnoseOpen, setDiagnoseOpen] = useState(false);
  const [diagnoseData, setDiagnoseData] = useState(null);
  const [diagnoseLoading, setDiagnoseLoading] = useState(false);
  const [pushingNow, setPushingNow] = useState(null);

  const handlePushNow = async (advertId) => {
    setPushingNow(advertId);
    try {
      const r = await axios.post(`${API}/adverts/${advertId}/queue/push-now`, null, { timeout: 60000 });
      const d = r.data || {};
      if (d.pushed_count > 0 && d.failed_count === 0) {
        toast.success(`Geskuif na Geskeduleer (${d.pushed_count} platform(s)).`);
      } else if (d.pushed_count > 0 && d.failed_count > 0) {
        toast.warning(`${d.pushed_count} platform(s) geskuif, ${d.failed_count} het misluk. Sien Diagnose vir besonderhede.`);
      } else {
        const firstErr = (d.results || []).find(x => !x.ok)?.message || 'Geen platforms is geskuif nie.';
        toast.error(firstErr);
      }
      fetchQueue(false);
      fetchAdverts();
    } catch (e) {
      toast.error(e.response?.data?.detail || e?.message || 'Kon nie skuif nie.');
    } finally {
      setPushingNow(null);
    }
  };

  const handleDiagnose = async () => {
    setDiagnoseOpen(true);
    setDiagnoseLoading(true);
    setDiagnoseData(null);
    try {
      const r = await axios.get(`${API}/adverts/queue/diagnose`, { timeout: 15000 });
      setDiagnoseData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || e?.message || 'Kon nie diagnose nie.');
      setDiagnoseOpen(false);
    } finally {
      setDiagnoseLoading(false);
    }
  };

  const togglePlatform = (id) => {
    setForm(prev => {
      const set = new Set(prev.platforms);
      if (set.has(id)) set.delete(id); else set.add(id);
      return { ...prev, platforms: Array.from(set) };
    });
  };

  // YouTube + TikTok require video (or audio that we'll auto-convert). Disable for image/none.
  const videoOnlyPlatform = (id) => id === 'youtube' || id === 'tiktok';
  const ytEligible = (mediaType) => mediaType === 'video' || mediaType === 'audio';

  const selectAll = () => {
    const eligible = PLATFORMS.filter(p => !videoOnlyPlatform(p.id) || ytEligible(form.media_type));
    const allSelected = form.platforms.length === eligible.length &&
      eligible.every(p => form.platforms.includes(p.id));
    setForm(prev => ({ ...prev, platforms: allSelected ? [] : eligible.map(p => p.id) }));
  };

  // Auto-remove video-only platforms from selection if media changes to a non-video/audio type
  useEffect(() => {
    if (!ytEligible(form.media_type)) {
      const toDrop = form.platforms.filter(p => videoOnlyPlatform(p));
      if (toDrop.length > 0) {
        setForm(prev => ({ ...prev, platforms: prev.platforms.filter(p => !videoOnlyPlatform(p)) }));
      }
    }
  }, [form.media_type, form.platforms]);

  const detectByMagicBytes = async (file) => {
    // Read first 16 bytes and check common audio/image/video magic numbers.
    try {
      const buf = new Uint8Array(await file.slice(0, 16).arrayBuffer());
      const hex = Array.from(buf).map(b => b.toString(16).padStart(2, '0')).join('');
      const ascii = String.fromCharCode(...buf);
      // OGG (Vorbis/Opus): 'OggS'
      if (ascii.startsWith('OggS')) return 'audio';
      // ID3 mp3
      if (ascii.startsWith('ID3')) return 'audio';
      // MP3 frame sync
      if (buf[0] === 0xff && (buf[1] & 0xe0) === 0xe0) return 'audio';
      // WAV: RIFF....WAVE
      if (ascii.startsWith('RIFF') && ascii.substring(8, 12) === 'WAVE') return 'audio';
      // FLAC: 'fLaC'
      if (ascii.startsWith('fLaC')) return 'audio';
      // AAC ADTS
      if (buf[0] === 0xff && (buf[1] & 0xf6) === 0xf0) return 'audio';
      // M4A/MP4 ('ftyp' at offset 4)
      if (ascii.substring(4, 8) === 'ftyp') {
        const brand = ascii.substring(8, 12);
        if (brand.startsWith('M4A') || brand === 'M4B ' || brand === 'mp42' || brand === 'isom' || brand === 'qt  ') {
          // Could be audio or video; default to video unless brand says M4A
          return brand.startsWith('M4A') || brand === 'M4B ' ? 'audio' : 'video';
        }
      }
      // JPEG
      if (hex.startsWith('ffd8ff')) return 'image';
      // PNG
      if (hex.startsWith('89504e47')) return 'image';
      // GIF
      if (ascii.startsWith('GIF8')) return 'image';
      // WebP: RIFF....WEBP
      if (ascii.startsWith('RIFF') && ascii.substring(8, 12) === 'WEBP') return 'image';
    } catch (e) {
      console.debug('[detect-media] header sniff failed:', e?.message);
    }
    return null;
  };

  const handleFileSelect = async (file) => {
    if (!file) return;
    // Determine media type by mime; fall back to filename extension; then to
    // magic-byte sniffing (handles WhatsApp .opus exports that arrive with no
    // extension and no content_type).
    const mime = (file.type || '').toLowerCase();
    const name = (file.name || '').toLowerCase();
    let media_type = null;
    if (mime.startsWith('image/') || /\.(jpe?g|png|gif|webp|heic|heif|bmp)$/i.test(name)) {
      media_type = 'image';
    } else if (mime.startsWith('video/') || /\.(mp4|mov|m4v|webm|mkv|avi)$/i.test(name)) {
      media_type = 'video';
    } else if (mime.startsWith('audio/') || /\.(mp3|wav|m4a|aac|aiff|flac|ogg|opus|amr|wma)$/i.test(name)) {
      media_type = 'audio';
    } else {
      media_type = await detectByMagicBytes(file);
    }
    if (!media_type) {
      toast.error('Ongesteunde lêer tipe. Slegs prent, video of klank.');
      return;
    }

    const mbCap = media_type === 'image' ? limits.image_mb : media_type === 'video' ? limits.video_mb : limits.audio_mb;
    const sizeMb = file.size / (1024 * 1024);
    if (sizeMb > mbCap) {
      toast.error(`Lêer is te groot (${sizeMb.toFixed(1)} MB). Maks vir ${media_type}: ${mbCap} MB.`);
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/adverts/upload-media`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => {
          if (e.total) setUploadProgress(Math.round((e.loaded / e.total) * 100));
        },
      });
      setForm(prev => ({
        ...prev,
        media_url: r.data.media_url,
        media_type: r.data.media_type,
        media_filename: r.data.media_filename,
        size_bytes: r.data.size_bytes,
      }));
      toast.success('Media opgelaai.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Oplaai het misluk.');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const removeMedia = () => {
    setForm(prev => ({ ...prev, media_url: null, media_type: null, media_filename: null, size_bytes: 0 }));
    setLinkedBookId('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const validateBeforePublish = () => {
    if (!form.title.trim()) { toast.error('Voer ’n titel in.'); return false; }
    if (!form.caption.trim()) { toast.error('Voer ’n byskrif in.'); return false; }
    if (form.platforms.length === 0) { toast.error('Kies ten minste een platform.'); return false; }
    return true;
  };

  const handleSaveDraft = async () => {
    if (!form.title.trim()) { toast.error('Voer ’n titel in.'); return; }
    setSavingDraft(true);
    try {
      if (form.id) {
        await axios.put(`${API}/adverts/${form.id}`, {
          title: form.title, caption: form.caption, platforms: form.platforms,
          media_url: form.media_url, media_type: form.media_type,
          media_filename: form.media_filename, size_bytes: form.size_bytes,
        });
        toast.success('Konsep opgedateer.');
      } else {
        const r = await axios.post(`${API}/adverts`, { ...form, status: 'draft' });
        setForm(prev => ({ ...prev, id: r.data.advert.id }));
        toast.success('Konsep gestoor.');
      }
      fetchAdverts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kon nie stoor nie.');
    } finally {
      setSavingDraft(false);
    }
  };

  const openConfirmPublish = () => {
    if (!validateBeforePublish()) return;
    setConfirmOpen(true);
  };

  const handlePublish = async () => {
    setConfirmOpen(false);
    setPublishing(true);
    try {
      let advertId = form.id;
      if (!advertId) {
        const create = await axios.post(`${API}/adverts`, { ...form, status: 'draft' });
        advertId = create.data.advert.id;
      } else {
        // ensure latest fields saved
        await axios.put(`${API}/adverts/${advertId}`, {
          title: form.title, caption: form.caption, platforms: form.platforms,
          media_url: form.media_url, media_type: form.media_type,
          media_filename: form.media_filename, size_bytes: form.size_bytes,
        });
      }
      const r = await axios.post(`${API}/adverts/${advertId}/publish`);
      setResultDialog({ advert: r.data.advert, results: r.data.results });
      setForm(EMPTY_FORM);
      setLinkedBookId('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      fetchAdverts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Publisering het misluk.');
    } finally {
      setPublishing(false);
    }
  };

  const openSchedule = () => {
    if (!validateBeforePublish()) return;
    // Default: 1 hour from now, formatted for datetime-local input
    const d = new Date(Date.now() + 60 * 60 * 1000);
    const pad = (n) => String(n).padStart(2, '0');
    const local = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    setScheduledAt(local);
    setScheduleOpen(true);
  };

  const handleSchedule = async () => {
    if (!scheduledAt) { toast.error('Kies ’n datum en tyd.'); return; }
    const when = new Date(scheduledAt);
    if (isNaN(when.getTime())) { toast.error('Ongeldige datum.'); return; }
    if (when.getTime() <= Date.now()) {
      toast.error('Skedulering tyd moet in die toekoms wees.');
      return;
    }
    setScheduleOpen(false);
    setScheduling(true);
    try {
      let advertId = form.id;
      if (!advertId) {
        const create = await axios.post(`${API}/adverts`, { ...form, status: 'draft' });
        advertId = create.data.advert.id;
      } else {
        await axios.put(`${API}/adverts/${advertId}`, {
          title: form.title, caption: form.caption, platforms: form.platforms,
          media_url: form.media_url, media_type: form.media_type,
          media_filename: form.media_filename, size_bytes: form.size_bytes,
        });
      }
      const r = await axios.post(`${API}/adverts/${advertId}/schedule`,
        { scheduled_for: when.toISOString() });
      setResultDialog({ advert: r.data.advert, results: r.data.results });
      setForm(EMPTY_FORM);
      setLinkedBookId('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      setTab('scheduled');
      fetchAdverts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Skedulering het misluk.');
    } finally {
      setScheduling(false);
    }
  };

  const handleCancelSchedule = async (id) => {
    if (!window.confirm('Kanselleer hierdie geskeduleerde plasing? Buffer sal die geskeduleerde pos verwyder en die advertensie sal teruggaan na konsep.')) return;
    setCancellingSchedule(id);
    try {
      const r = await axios.post(`${API}/adverts/${id}/unschedule`);
      const failedCount = (r.data.failed_to_cancel || []).length;
      if (failedCount > 0) {
        toast.warning(`Gekanselleer maar ${failedCount} Buffer pos(te) kon nie verwyder word nie — kontroleer Buffer dashboard.`);
      } else {
        toast.success('Geskedulering gekanselleer. Advertensie is nou ’n konsep.');
      }
      fetchAdverts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kon nie kanselleer nie.');
    } finally {
      setCancellingSchedule(null);
    }
  };


  const handleDuplicate = async (id) => {
    try {
      const r = await axios.post(`${API}/adverts/${id}/duplicate`);
      toast.success('Advertensie gedupliseer.');
      setForm({
        id: r.data.id,
        title: r.data.title,
        caption: r.data.caption,
        platforms: r.data.platforms,
        media_url: r.data.media_url,
        media_type: r.data.media_type,
        media_filename: r.data.media_filename,
        size_bytes: r.data.size_bytes,
      });
      setTab('new');
      fetchAdverts();
    } catch (e) {
      toast.error('Kon nie dupliseer nie.');
    }
  };

  const handleRepost = async (id) => {
    try {
      const r = await axios.post(`${API}/adverts/${id}/repost`);
      setResultDialog({ advert: r.data.advert, results: r.data.results });
      fetchAdverts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Herplaas het misluk.');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Is jy seker jy wil hierdie konsep uitvee?')) return;
    try {
      await axios.delete(`${API}/adverts/${id}`);
      toast.success('Konsep uitgevee.');
      fetchAdverts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kon nie uitvee nie.');
    }
  };

  const handleLoadDraft = (ad) => {
    setForm({
      id: ad.id,
      title: ad.title || '',
      caption: ad.caption || '',
      platforms: ad.platforms || [],
      media_url: ad.media_url || null,
      media_type: ad.media_type || null,
      media_filename: ad.media_filename || null,
      size_bytes: ad.size_bytes || 0,
    });
    setTab('new');
  };

  const handleOpenHistory = async (id) => {
    try {
      const r = await axios.get(`${API}/adverts/${id}`);
      setHistoryDialog(r.data);
    } catch (e) {
      toast.error('Kon nie geskiedenis laai nie.');
    }
  };

  // --- Render helpers ---
  const renderMediaPreview = () => {
    if (!form.media_url) return (
      <div className="border-2 border-dashed border-orange-200 rounded-lg p-8 text-center text-sm text-gray-500">
        Geen media opgelaai nie.
      </div>
    );
    if (form.media_type === 'image') {
      return <img src={form.media_url} alt="preview" className="max-h-72 rounded-lg border" data-testid="advert-media-preview-img" />;
    }
    if (form.media_type === 'video') {
      return <video src={form.media_url} controls className="max-h-72 rounded-lg border w-full" data-testid="advert-media-preview-video" />;
    }
    if (form.media_type === 'audio') {
      return <audio src={form.media_url} controls className="w-full" data-testid="advert-media-preview-audio" />;
    }
    return null;
  };

  const platformIcon = (id) => {
    const found = PLATFORMS.find(p => p.id === id);
    if (!found) return null;
    const Icon = found.icon;
    return <Icon className={`h-4 w-4 ${found.color}`} />;
  };

  return (
    <div className="space-y-6" data-testid="adverteer-page">
      <div className="flex items-center gap-3">
        <Megaphone className="h-7 w-7 text-orange-600" />
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Adverteer</h2>
          <p className="text-sm text-gray-600">Skep en publiseer advertensies na Facebook, Instagram, TikTok en YouTube.</p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={(v) => { setTab(v); if (v === 'queue') fetchQueue(); }} className="w-full">
        <TabsList className="grid w-full grid-cols-5 max-w-2xl">
          <TabsTrigger value="new" data-testid="tab-new-advert">Nuwe Advertensie</TabsTrigger>
          <TabsTrigger value="drafts" data-testid="tab-drafts">Konsepte ({drafts.length})</TabsTrigger>
          <TabsTrigger value="queue" data-testid="tab-queue">Wagry ({queueItems.length})</TabsTrigger>
          <TabsTrigger value="scheduled" data-testid="tab-scheduled">Geskeduleer ({scheduled.length})</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">Geskiedenis</TabsTrigger>
        </TabsList>

        {/* ============ NEW ADVERT ============ */}
        <TabsContent value="new" className="space-y-6">
          <Card className="border-orange-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-orange-700">
                <FileText className="h-5 w-5" /> Advertensie Besonderhede
              </CardTitle>
              <CardDescription>
                Lêer limiete — Prent: {limits.image_mb} MB · Video: {limits.video_mb} MB · Klank: {limits.audio_mb} MB
                (klank word outomaties na video omgeskakel vir sosiale media).
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="rounded-lg border border-orange-100 bg-orange-50/40 p-3 space-y-2">
                <Label htmlFor="link-book-select" className="flex items-center gap-2 text-orange-700">
                  <BookOpen className="h-4 w-4" /> Skakel met Boek (opsioneel)
                </Label>
                <p className="text-xs text-gray-600">
                  Kies &rsquo;n gepubliseerde boek om die titel, byskrif en voorblad outomaties in te vul.
                </p>
                <div className="flex items-center gap-2">
                  <Popover open={bookPickerOpen} onOpenChange={setBookPickerOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        id="link-book-select"
                        type="button"
                        variant="outline"
                        role="combobox"
                        aria-expanded={bookPickerOpen}
                        className="flex-1 justify-between bg-white font-normal"
                        data-testid="advert-link-book-select"
                      >
                        <span className="truncate text-left">
                          {linkedBookId
                            ? (() => {
                                const b = publishedBooks.find(x => x.id === linkedBookId);
                                return b ? `${b.boek_naam}${b.skrywer_naam ? ` — ${b.skrywer_naam}` : ''}` : 'Boek gekies';
                              })()
                            : '— Kies \u2019n boek —'}
                        </span>
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="p-0 w-[--radix-popover-trigger-width] max-w-[600px]" align="start">
                      <Command
                        filter={(value, search) => {
                          // value is the lowercased boek_naam+skrywer string we set on each item.
                          // Match if every space-separated token in the search appears anywhere.
                          if (!search) return 1;
                          const v = value.toLowerCase();
                          const tokens = search.toLowerCase().split(/\s+/).filter(Boolean);
                          return tokens.every(t => v.includes(t)) ? 1 : 0;
                        }}
                      >
                        <CommandInput placeholder="Soek boek of skrywer..." data-testid="advert-link-book-search" />
                        <CommandList>
                          <CommandEmpty>Geen boek pas by jou soektog nie.</CommandEmpty>
                          <CommandGroup>
                            {publishedBooks.map(b => {
                              const display = `${b.boek_naam}${b.skrywer_naam ? ` — ${b.skrywer_naam}` : ''}`;
                              const filterValue = `${b.boek_naam || ''} ${b.skrywer_naam || ''}`.toLowerCase();
                              return (
                                <CommandItem
                                  key={b.id}
                                  value={filterValue}
                                  onSelect={() => {
                                    handleLinkBook(b.id);
                                    setBookPickerOpen(false);
                                  }}
                                  data-testid={`advert-link-book-option-${b.id}`}
                                >
                                  <Check className={`mr-2 h-4 w-4 ${linkedBookId === b.id ? 'opacity-100' : 'opacity-0'}`} />
                                  <span className="truncate">{display}</span>
                                </CommandItem>
                              );
                            })}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                  {linkedBookId && (
                    <Button type="button" variant="ghost" size="sm"
                      onClick={() => {
                        setLinkedBookId('');
                        // Also wipe the fields applyBookLink auto-filled so the form is genuinely fresh.
                        setForm(prev => ({
                          ...prev,
                          title: '',
                          caption: '',
                          media_url: '',
                          media_type: 'image',
                          media_filename: '',
                          size_bytes: 0,
                        }));
                        toast.success('Boekskakel verwyder. Vorm is skoongemaak.');
                      }}
                      data-testid="advert-link-book-clear">
                      Maak los
                    </Button>
                  )}
                </div>
              </div>

              <div>
                <Label htmlFor="advert-title">Advertensie Titel</Label>
                <Input id="advert-title" value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="bv. Nuwe boek vrystelling - Junie"
                  data-testid="advert-title-input" />
              </div>

              <div>
                <Label htmlFor="advert-caption">Byskrif / Teks vir Sosiale Media</Label>
                <Textarea id="advert-caption" rows={4} value={form.caption}
                  onChange={(e) => setForm({ ...form, caption: e.target.value })}
                  placeholder="Skryf jou pos teks hier..."
                  data-testid="advert-caption-input" />
                <p className="text-xs text-gray-500 mt-1">{form.caption.length} karakters (X beperk tot 280).</p>
              </div>

              <div>
                <Label>Media (Prent / Video / Klank)</Label>
                <div className="flex items-center gap-3 flex-wrap">
                  <input ref={fileInputRef} type="file"
                    className="hidden"
                    onChange={(e) => handleFileSelect(e.target.files?.[0])}
                    data-testid="advert-file-input" />
                  <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()}
                    disabled={uploading} data-testid="advert-upload-btn">
                    {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                    Kies Lêer
                  </Button>
                  {form.media_url && (
                    <>
                      <Badge variant="secondary" className="gap-1">
                        {form.media_type === 'image' && <ImageIcon className="h-3 w-3" />}
                        {form.media_type === 'video' && <Video className="h-3 w-3" />}
                        {form.media_type === 'audio' && <Music className="h-3 w-3" />}
                        {form.media_filename}
                        {form.size_bytes > 0 && ` (${(form.size_bytes / (1024 * 1024)).toFixed(2)} MB)`}
                      </Badge>
                      <Button type="button" variant="ghost" size="sm" onClick={removeMedia}
                        data-testid="advert-remove-media-btn">Verwyder</Button>
                    </>
                  )}
                </div>
                {uploading && <Progress value={uploadProgress} className="mt-2" />}
              </div>

              <div>
                <Label>Kies Platforms</Label>
                <div className="flex flex-wrap items-center gap-4 mt-2">
                  {PLATFORMS.map(p => {
                    const Icon = p.icon;
                    const checked = form.platforms.includes(p.id);
                    // YouTube + TikTok require video (or audio that we'll auto-convert).
                    const disabled = videoOnlyPlatform(p.id) && (!form.media_type || form.media_type === 'image');
                    return (
                      <label key={p.id}
                        className={`flex items-center gap-2 ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                        title={disabled ? `${p.label} vereis ’n video of klanklêer` : ''}>
                        <Checkbox checked={checked}
                          disabled={disabled}
                          onCheckedChange={() => !disabled && togglePlatform(p.id)}
                          data-testid={`platform-${p.id}-checkbox`} />
                        <Icon className={`h-4 w-4 ${p.color}`} />
                        <span className="text-sm">{p.label}</span>
                        {disabled && <span className="text-xs text-gray-500">(slegs video/klank)</span>}
                      </label>
                    );
                  })}
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={(() => {
                        const eligible = PLATFORMS.filter(p => !videoOnlyPlatform(p.id) || ytEligible(form.media_type));
                        return eligible.length > 0 && eligible.every(p => form.platforms.includes(p.id));
                      })()}
                      onCheckedChange={selectAll}
                      data-testid="platform-select-all-checkbox" />
                    <span className="text-sm font-semibold">Kies Alles</span>
                  </label>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Preview */}
          <Card className="border-orange-200">
            <CardHeader>
              <CardTitle className="text-orange-700">Voorskou</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {renderMediaPreview()}
              <div>
                <p className="font-semibold text-gray-900">{form.title || '— Titel —'}</p>
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{form.caption || '— Byskrif —'}</p>
              </div>
              <div className="flex gap-2 flex-wrap">
                {form.platforms.length === 0 && <Badge variant="outline">Geen platforms gekies nie</Badge>}
                {form.platforms.map(pid => {
                  const p = PLATFORMS.find(x => x.id === pid);
                  if (!p) return null;
                  const Icon = p.icon;
                  return (
                    <Badge key={pid} variant="secondary" className="gap-1">
                      <Icon className={`h-3 w-3 ${p.color}`} />
                      {p.label}
                    </Badge>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          <div className="flex flex-wrap gap-3">
            <Button onClick={handleSaveDraft} disabled={savingDraft || publishing || scheduling} variant="outline"
              data-testid="save-draft-btn">
              {savingDraft ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Edit className="h-4 w-4 mr-2" />}
              Stoor as Konsep
            </Button>
            <Button onClick={openConfirmPublish} disabled={publishing || uploading || scheduling}
              className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700"
              data-testid="publish-now-btn">
              {publishing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
              Pos Nou
            </Button>
            <Button onClick={openSchedule} disabled={publishing || uploading || scheduling || queueing}
              className="bg-purple-600 hover:bg-purple-700 text-white"
              data-testid="schedule-btn">
              {scheduling ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CalendarClock className="h-4 w-4 mr-2" />}
              Plaas Later
            </Button>
            <Button onClick={openQueueDialog} disabled={publishing || uploading || scheduling || queueing}
              variant="outline"
              className="border-amber-500 text-amber-700 hover:bg-amber-50"
              data-testid="queue-btn"
              title="Voeg by die plaaslike wagry — werk om Buffer se 10-plasings-limiet">
              {queueing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ListOrdered className="h-4 w-4 mr-2" />}
              Voeg by Wagry
            </Button>
            {form.id && (
              <Button variant="ghost" onClick={() => { setForm(EMPTY_FORM); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                data-testid="reset-form-btn">
                Maak Skoon
              </Button>
            )}
          </div>
        </TabsContent>

        {/* ============ DRAFTS ============ */}
        <TabsContent value="drafts">
          <Card className="border-orange-200">
            <CardHeader>
              <CardTitle className="text-orange-700">Konsepte</CardTitle>
              <CardDescription>Bestuur of publiseer gestoorde konsepte.</CardDescription>
            </CardHeader>
            <CardContent>
              {loadingList ? (
                <div className="flex items-center gap-2 text-gray-500"><Loader2 className="h-4 w-4 animate-spin" /> Laai...</div>
              ) : drafts.length === 0 ? (
                <p className="text-sm text-gray-500">Geen konsepte nie.</p>
              ) : (
                <div className="space-y-3">
                  {drafts.map(ad => (
                    <div key={ad.id} className="border rounded-lg p-3 flex flex-col sm:flex-row sm:items-center gap-3"
                      data-testid={`draft-row-${ad.id}`}>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold truncate">{ad.title}</p>
                        <p className="text-xs text-gray-500 truncate">{ad.caption}</p>
                        <div className="flex gap-1 mt-1">
                          {(ad.platforms || []).map(pid => <span key={pid}>{platformIcon(pid)}</span>)}
                        </div>
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        <Button size="sm" variant="outline" onClick={() => setPreviewAdvert(ad)}
                          data-testid={`draft-preview-${ad.id}`}>
                          <Eye className="h-3 w-3 mr-1" /> Voorskou
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => handleLoadDraft(ad)}
                          data-testid={`edit-draft-${ad.id}`}>
                          <Edit className="h-3 w-3 mr-1" /> Wysig
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => handleDuplicate(ad.id)}>
                          <Copy className="h-3 w-3 mr-1" /> Dupliseer
                        </Button>
                        <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDelete(ad.id)}
                          data-testid={`delete-draft-${ad.id}`}>
                          <Trash2 className="h-3 w-3 mr-1" /> Vee uit
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ============ QUEUE (Wagry) ============ */}
        <TabsContent value="queue">
          <Card className="border-amber-200">
            <CardHeader>
              <CardTitle className="text-amber-700 flex items-center gap-2">
                <Inbox className="h-5 w-5" /> Wagry (Plaaslike Wagry)
              </CardTitle>
              <CardDescription>
                Plaaslike wagry werk rondom Buffer se 10-plasings-per-kanaal limiet. Stelsel druk outomaties die volgende advertensie na Buffer sodra ’n gleuf oop is (kontroleer elke 15 min). Hoogstens een plasing per platform per dag — botsende dae word outomaties na die volgende vrye dag geskuif.
                <br /><strong>Weeklikse aanvul:</strong> elke Sondag 07:00 vul die stelsel outomaties Geskeduleer op tot 8 advertensies deur die volgende items (vroegste datum eerste) uit Wagry te skuif. Wanneer 8 of minder in Wagry oor is, word ’n herinnering-e-pos gestuur.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {queueStatusData && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="queue-status-grid">
                  {Object.entries(queueStatusData.channels).map(([p, c]) => {
                    const Icon = (PLATFORMS.find(x => x.id === p) || {}).icon;
                    const colorCls = (PLATFORMS.find(x => x.id === p) || {}).color || '';
                    return (
                      <div key={p} className="border rounded-lg p-3 bg-white" data-testid={`queue-channel-${p}`}>
                        <div className="flex items-center gap-2 mb-2">
                          {Icon && <Icon className={`h-4 w-4 ${colorCls}`} />}
                          <span className="font-semibold capitalize">{p}</span>
                        </div>
                        <div className="text-xs text-gray-600 space-y-0.5">
                          <p>In Buffer: <strong>{c.in_buffer}</strong> / {c.cap}</p>
                          <p>Wagry plaaslik: <strong className="text-amber-700">{c.queued}</strong></p>
                          <p>Vry gleuwe: <strong className="text-emerald-700">{c.slots_free}</strong></p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              <div className="flex items-center justify-between">
                <p className="text-xs text-gray-500">
                  Totaal in wagry: <strong>{queueStatusData?.total_queued || 0}</strong> · Verwerk-horison: {queueStatusData?.push_horizon_hours || 48}u · Outomatiese spasie: {queueStatusData?.default_stagger_hours || 24}u
                </p>
                <div className="flex gap-2">
                  <div className="inline-flex rounded-md border bg-white overflow-hidden" data-testid="queue-view-toggle">
                    <button
                      type="button"
                      onClick={() => setQueueView('calendar')}
                      className={`px-3 py-1.5 text-xs ${queueView === 'calendar' ? 'bg-amber-500 text-white' : 'text-gray-600 hover:bg-gray-50'}`}
                      data-testid="queue-view-calendar-btn"
                    >
                      Kalender
                    </button>
                    <button
                      type="button"
                      onClick={() => setQueueView('list')}
                      className={`px-3 py-1.5 text-xs border-l ${queueView === 'list' ? 'bg-amber-500 text-white' : 'text-gray-600 hover:bg-gray-50'}`}
                      data-testid="queue-view-list-btn"
                    >
                      Lys
                    </button>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => fetchQueue(true)} data-testid="queue-refresh-btn">
                    <RefreshCw className="h-3 w-3 mr-1" /> Herlaai
                  </Button>
                  <Button size="sm" variant="outline" onClick={handleDiagnose}
                    className="border-blue-500 text-blue-700"
                    data-testid="queue-diagnose-btn">
                    <Info className="h-3 w-3 mr-1" /> Diagnose
                  </Button>
                  <Button size="sm" variant="outline" onClick={handleFlushQueue}
                    className="border-amber-500 text-amber-700"
                    data-testid="queue-flush-btn">
                    <Send className="h-3 w-3 mr-1" /> Verwerk Nou
                  </Button>
                  <Button size="sm" variant="outline" onClick={handleWeeklyTopup} disabled={topupBusy}
                    className="border-purple-500 text-purple-700"
                    data-testid="queue-topup-btn">
                    <RefreshCw className={`h-3 w-3 mr-1 ${topupBusy ? 'animate-spin' : ''}`} /> Vul Geskeduleer aan
                  </Button>
                </div>
              </div>
              {queueView === 'calendar' ? (
                <WagryCalendar
                  adverts={[...queueItems, ...scheduled.filter(s => !queueItems.find(q => q.id === s.id))]}
                  apiBase={API}
                  onChange={() => { fetchQueue(); fetchAdverts(); }}
                  onPreview={(ad) => setPreviewAdvert(ad)}
                />
              ) : queueItems.length === 0 ? (
                <p className="text-sm text-gray-500">Geen items in die wagry nie.</p>
              ) : (
                <div className="space-y-3">
                  {queueItems.map(ad => (
                    <div key={ad.id} className="border rounded-lg p-3" data-testid={`queue-row-${ad.id}`}>
                      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-semibold truncate">{ad.title}</p>
                            <Badge className={STATUS_BADGE[ad.status]?.cls || 'bg-gray-200'}>
                              {STATUS_BADGE[ad.status]?.label || ad.status}
                            </Badge>
                          </div>
                          {ad.scheduled_for && (
                            <p className="text-xs text-gray-600 mt-0.5">
                              <CalendarClock className="inline h-3 w-3 mr-1" />
                              Doel: <strong>{new Date(ad.scheduled_for).toLocaleString('af-ZA')}</strong>
                            </p>
                          )}
                          <div className="flex flex-wrap gap-2 mt-1">
                            {Object.entries(ad.queue_plan || {}).map(([p, info]) => {
                              const Icon = (PLATFORMS.find(x => x.id === p) || {}).icon;
                              const stateColor = {
                                queued: 'bg-amber-100 text-amber-800',
                                sent: 'bg-purple-100 text-purple-800',
                                published: 'bg-green-100 text-green-800',
                                failed: 'bg-red-100 text-red-800',
                                cancelled: 'bg-gray-200 text-gray-700',
                              }[info?.state] || 'bg-gray-200';
                              return (
                                <span key={p} className={`text-xs px-2 py-0.5 rounded ${stateColor} inline-flex items-center gap-1`}>
                                  {Icon && <Icon className="h-3 w-3" />}
                                  {p}: {info?.state}
                                </span>
                              );
                            })}
                          </div>
                          {Object.entries(ad.queue_plan || {})
                            .filter(([, info]) => info?.error)
                            .map(([p, info]) => (
                              <p key={p} className="text-xs text-red-600 mt-1">
                                {p}: {info.error}
                              </p>
                            ))}
                        </div>
                        <div className="flex gap-2 flex-wrap">
                          <Button size="sm" variant="outline" onClick={() => setPreviewAdvert(ad)}
                            data-testid={`queue-preview-${ad.id}`}>
                            <Eye className="h-3 w-3 mr-1" /> Voorskou
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => handleOpenHistory(ad.id)}>
                            <History className="h-3 w-3 mr-1" /> Detail
                          </Button>
                          {/* One-click: skip the horizon wait, push this advert's queued platforms to Buffer now */}
                          {Object.values(ad.queue_plan || {}).some(p => p?.state === 'queued') && (
                            <Button size="sm" variant="outline"
                              className="border-emerald-500 text-emerald-700 hover:bg-emerald-50"
                              disabled={pushingNow === ad.id}
                              onClick={() => handlePushNow(ad.id)}
                              data-testid={`queue-push-now-${ad.id}`}>
                              {pushingNow === ad.id
                                ? <Loader2 className="h-3 w-3 animate-spin mr-1" />
                                : <CalendarClock className="h-3 w-3 mr-1" />}
                              Skuif na Geskeduleer
                            </Button>
                          )}
                          <Button size="sm" variant="outline" className="text-red-600"
                            disabled={removingFromQueue === ad.id}
                            onClick={() => handleRemoveFromQueue(ad.id)}
                            data-testid={`queue-remove-${ad.id}`}>
                            {removingFromQueue === ad.id
                              ? <Loader2 className="h-3 w-3 animate-spin mr-1" />
                              : <XCircle className="h-3 w-3 mr-1" />}
                            Verwyder
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ============ SCHEDULED ============ */}
        <TabsContent value="scheduled">
          <Card className="border-orange-200">
            <CardHeader>
              <CardTitle className="text-orange-700 flex items-center gap-2">
                <CalendarClock className="h-5 w-5" /> Geskeduleerde Advertensies
              </CardTitle>
              <CardDescription>
                Advertensies wat outomaties deur Buffer geplaas sal word op die gekose datum/tyd.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loadingList ? (
                <div className="flex items-center gap-2 text-gray-500"><Loader2 className="h-4 w-4 animate-spin" /> Laai...</div>
              ) : scheduled.length === 0 ? (
                <p className="text-sm text-gray-500">Geen geskeduleerde advertensies nie.</p>
              ) : (
                <div className="space-y-3">
                  {scheduled.map(ad => (
                    <div key={ad.id} className="border rounded-lg p-3 flex flex-col sm:flex-row sm:items-center gap-3 bg-purple-50/50"
                      data-testid={`scheduled-row-${ad.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-semibold truncate">{ad.title}</p>
                          <Badge className={STATUS_BADGE.scheduled.cls}>{STATUS_BADGE.scheduled.label}</Badge>
                        </div>
                        <p className="text-xs text-gray-600 mt-0.5">
                          <CalendarClock className="inline h-3 w-3 mr-1" />
                          Plaas op: <strong>{ad.scheduled_for ? new Date(ad.scheduled_for).toLocaleString('af-ZA') : '—'}</strong>
                        </p>
                        <p className="text-xs text-gray-500">Geskep deur {ad.created_by_name || '—'}</p>
                        <div className="flex gap-1 mt-1">
                          {(ad.platforms || []).map(pid => <span key={pid}>{platformIcon(pid)}</span>)}
                        </div>
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        <Button size="sm" variant="outline" onClick={() => handleOpenHistory(ad.id)}
                          data-testid={`view-scheduled-${ad.id}`}>
                          <History className="h-3 w-3 mr-1" /> Detail
                        </Button>
                        <Button size="sm" variant="outline" className="text-red-600"
                          disabled={cancellingSchedule === ad.id}
                          onClick={() => handleCancelSchedule(ad.id)}
                          data-testid={`cancel-schedule-${ad.id}`}>
                          {cancellingSchedule === ad.id
                            ? <Loader2 className="h-3 w-3 animate-spin mr-1" />
                            : <XCircle className="h-3 w-3 mr-1" />}
                          Kanselleer
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ============ HISTORY ============ */}
        <TabsContent value="history">
          <Card className="border-orange-200">
            <CardHeader>
              <CardTitle className="text-orange-700 flex items-center gap-2"><History className="h-5 w-5" /> Advertensie Geskiedenis</CardTitle>
              <CardDescription>Alle gepubliseerde of probeerde advertensies.</CardDescription>
            </CardHeader>
            <CardContent>
              {loadingList ? (
                <div className="flex items-center gap-2 text-gray-500"><Loader2 className="h-4 w-4 animate-spin" /> Laai...</div>
              ) : adverts.filter(a => a.status !== 'draft').length === 0 ? (
                <p className="text-sm text-gray-500">Nog geen advertensies geplaas nie.</p>
              ) : (
                <div className="space-y-3">
                  {adverts.filter(a => a.status !== 'draft').map(ad => (
                    <div key={ad.id} className="border rounded-lg p-3" data-testid={`history-row-${ad.id}`}>
                      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-semibold truncate">{ad.title}</p>
                            <Badge className={STATUS_BADGE[ad.status]?.cls || 'bg-gray-200'}>
                              {STATUS_BADGE[ad.status]?.label || ad.status}
                            </Badge>
                          </div>
                          <p className="text-xs text-gray-500">
                            Geskep deur {ad.created_by_name || '—'} · Geplaas deur {ad.posted_by_name || '—'} ·{' '}
                            {ad.posted_at ? new Date(ad.posted_at).toLocaleString('af-ZA') : '—'}
                          </p>
                          <div className="flex gap-1 mt-1">
                            {(ad.platforms || []).map(pid => <span key={pid}>{platformIcon(pid)}</span>)}
                          </div>
                        </div>
                        <div className="flex gap-2 flex-wrap">
                          <Button size="sm" variant="outline" onClick={() => setPreviewAdvert(ad)}
                            data-testid={`scheduled-preview-${ad.id}`}>
                            <Eye className="h-3 w-3 mr-1" /> Voorskou
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => handleOpenHistory(ad.id)}
                            data-testid={`view-history-${ad.id}`}>
                            <History className="h-3 w-3 mr-1" /> Detail
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => handleRepost(ad.id)}
                            data-testid={`repost-${ad.id}`}>
                            <RefreshCw className="h-3 w-3 mr-1" /> Herplaas
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => handleDuplicate(ad.id)}>
                            <Copy className="h-3 w-3 mr-1" /> Dupliseer
                          </Button>
                        </div>
                      </div>
                      {(ad.last_results || []).length > 0 && (
                        <div className="mt-2 space-y-1">
                          {ad.last_results.map((r, idx) => (
                            <div key={idx} className="flex items-center gap-2 text-xs">
                              {RESULT_ICON[r.status]}
                              <span className="font-medium">{r.platform}</span>
                              <span className="text-gray-600">{r.message}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Confirmation Dialog */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent data-testid="publish-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Bevestig Publisering</DialogTitle>
            <DialogDescription>Is jy seker jy wil hierdie advertensie nou plaas?</DialogDescription>
          </DialogHeader>
          <div className="text-sm text-gray-700">
            <p><strong>Titel:</strong> {form.title}</p>
            <p><strong>Platforms:</strong> {form.platforms.join(', ')}</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} data-testid="confirm-cancel-btn">Kanselleer</Button>
            <Button onClick={handlePublish} className="bg-orange-600 hover:bg-orange-700" data-testid="confirm-publish-btn">
              Ja, plaas nou
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <AdvertPreviewDialog
        advert={previewAdvert}
        open={!!previewAdvert}
        onClose={() => setPreviewAdvert(null)}
      />

      {/* Queue Dialog */}
      <Dialog open={queueDialogOpen} onOpenChange={setQueueDialogOpen}>
        <DialogContent data-testid="queue-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <Inbox className="h-5 w-5" /> Voeg by Wagry
            </DialogTitle>
            <DialogDescription>
              Hierdie advertensie word in die plaaslike wagry geplaas. Die stelsel stuur dit outomaties na Buffer sodra ’n gleuf oop is (Buffer gratis-vlak laat 10 geskeduleerde plasings per kanaal toe).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-gray-700">
              <p><strong>Titel:</strong> {form.title}</p>
              <p><strong>Platforms:</strong> {form.platforms.join(', ')}</p>
            </div>
            <div>
              <label className="text-sm font-medium" htmlFor="queue-target-at">
                Gekose datum en tyd
              </label>
              <input
                id="queue-target-at"
                type="datetime-local"
                value={queueTargetAt}
                onChange={(e) => setQueueTargetAt(e.target.value)}
                className="w-full mt-1 border rounded-md px-3 py-2 text-sm"
                required
                data-testid="queue-target-at-input"
              />
              <p className="text-xs text-gray-500 mt-1">
                Stelsel stuur die advertensie na Buffer op hierdie datum sodra ’n gleuf oop is. As jou gekose dag reeds ’n plasing het, skuif die stelsel outomaties na die volgende vrye dag op dieselfde tyd (een plasing per platform per dag).
              </p>
            </div>
            {queueStatusData && (
              <div className="text-xs text-gray-600 bg-amber-50 border border-amber-200 rounded p-2">
                Huidig in wagry: <strong>{queueStatusData.total_queued}</strong>. Vry Buffer gleuwe nou:{' '}
                {Object.entries(queueStatusData.channels).map(([p, c]) =>
                  <span key={p} className="ml-2"><strong>{p}</strong>: {c.slots_free}</span>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setQueueDialogOpen(false)} data-testid="queue-cancel-btn">
              Kanselleer
            </Button>
            <Button onClick={handleAddToQueue}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="queue-confirm-btn">
              <ListOrdered className="h-4 w-4 mr-2" />
              Voeg by Wagry
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Schedule Dialog */}
      <Dialog open={scheduleOpen} onOpenChange={setScheduleOpen}>
        <DialogContent data-testid="schedule-dialog">
          <DialogHeader>
            <DialogTitle>Skeduleer Advertensie</DialogTitle>
            <DialogDescription>
              Kies wanneer Buffer hierdie advertensie outomaties moet plaas.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-gray-700">
              <p><strong>Titel:</strong> {form.title}</p>
              <p><strong>Platforms:</strong> {form.platforms.join(', ')}</p>
            </div>
            <div>
              <label className="text-sm font-medium" htmlFor="scheduled-at">Datum &amp; Tyd</label>
              <input
                id="scheduled-at"
                type="datetime-local"
                value={scheduledAt}
                min={(() => {
                  const d = new Date(Date.now() + 5 * 60 * 1000);
                  const pad = (n) => String(n).padStart(2, '0');
                  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
                })()}
                onChange={(e) => setScheduledAt(e.target.value)}
                className="w-full mt-1 border rounded-md px-3 py-2 text-sm"
                data-testid="scheduled-at-input"
              />
              <p className="text-xs text-gray-500 mt-1">
                Plaaslike tyd word gebruik. Buffer plaas op die gekose oomblik.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setScheduleOpen(false)} data-testid="schedule-cancel-btn">
              Kanselleer
            </Button>
            <Button onClick={handleSchedule}
              className="bg-purple-600 hover:bg-purple-700 text-white"
              data-testid="schedule-confirm-btn">
              Skeduleer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Result Dialog */}
      <Dialog open={!!resultDialog} onOpenChange={(o) => !o && setResultDialog(null)}>
        <DialogContent data-testid="publish-result-dialog">
          <DialogHeader>
            <DialogTitle>Publiseer Resultate</DialogTitle>
            <DialogDescription>Status per platform:</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {(resultDialog?.results || []).map((r, idx) => (
              <div key={idx} className="border rounded p-2">
                <div className="flex items-center gap-2">
                  {RESULT_ICON[r.status]}
                  <span className="font-medium capitalize">{r.platform}</span>
                  <Badge variant="outline" className="capitalize">{r.status}</Badge>
                </div>
                <p className="text-xs text-gray-600 mt-1">{r.message}</p>
                <p className="text-xs text-gray-400 mt-0.5">
                  {r.posted_at ? new Date(r.posted_at).toLocaleString('af-ZA') : ''}
                </p>
                {r.post_url && <a href={r.post_url} target="_blank" rel="noreferrer" className="text-xs text-blue-600 underline">Bekyk pos</a>}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button onClick={() => setResultDialog(null)} data-testid="close-result-btn">Sluit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History Detail Dialog */}
      <Dialog open={!!historyDialog} onOpenChange={(o) => !o && setHistoryDialog(null)}>
        <DialogContent className="max-w-2xl" data-testid="history-detail-dialog">
          <DialogHeader>
            <DialogTitle>Geskiedenis - {historyDialog?.title}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto">
            {(historyDialog?.history || []).length === 0 ? (
              <p className="text-sm text-gray-500">Geen rekord nie.</p>
            ) : (
              historyDialog.history.map((h, idx) => (
                <div key={idx} className="border rounded p-2 text-sm">
                  <div className="flex items-center gap-2">
                    {RESULT_ICON[h.status]}
                    <span className="font-medium capitalize">{h.platform}</span>
                    <Badge variant="outline">{h.status}</Badge>
                    <span className="ml-auto text-xs text-gray-500">
                      {h.posted_at ? new Date(h.posted_at).toLocaleString('af-ZA') : ''}
                    </span>
                  </div>
                  <p className="text-xs text-gray-600 mt-1">{h.message}</p>
                  <p className="text-xs text-gray-400">Geplaas deur: {h.posted_by_name || '—'}</p>
                  {h.post_url && <a href={h.post_url} target="_blank" rel="noreferrer" className="text-xs text-blue-600 underline">Bekyk pos</a>}
                </div>
              ))
            )}
          </div>
          <DialogFooter>
            <Button onClick={() => setHistoryDialog(null)}>Sluit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Wagry Diagnose Dialog — shows exactly why each platform row is stuck */}
      <Dialog open={diagnoseOpen} onOpenChange={setDiagnoseOpen}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto" data-testid="queue-diagnose-dialog">
          <DialogHeader>
            <DialogTitle>Wagry Diagnose</DialogTitle>
            <DialogDescription>
              Wys presies hoekom elke platform-ry in die wagry vassit.
            </DialogDescription>
          </DialogHeader>
          {diagnoseLoading && (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Loader2 className="h-4 w-4 animate-spin" /> Laai diagnose...
            </div>
          )}
          {diagnoseData && (
            <div className="space-y-4 text-sm">
              {/* Global config */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 p-3 bg-gray-50 rounded border">
                <div>
                  <p className="text-xs text-gray-500">Buffer gekonfigureer</p>
                  <p className={`font-bold ${diagnoseData.buffer_configured ? 'text-emerald-700' : 'text-red-700'}`}>
                    {diagnoseData.buffer_configured ? 'JA' : 'NEE'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Verwerk-horison</p>
                  <p className="font-bold">{diagnoseData.horizon_hours}u</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Cap per kanaal</p>
                  <p className="font-bold">{diagnoseData.cap_per_channel}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Totaal items</p>
                  <p className="font-bold">{diagnoseData.total_items}</p>
                </div>
              </div>

              {/* Buffer channel config per platform */}
              {diagnoseData.buffer_channels_configured && (
                <div className="flex flex-wrap gap-2">
                  {Object.entries(diagnoseData.buffer_channels_configured).map(([plat, ok]) => (
                    <span key={plat} className={`text-xs px-2 py-1 rounded ${ok ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}`}>
                      {plat}: {ok ? 'gekoppel' : 'NIE gekoppel'}
                    </span>
                  ))}
                </div>
              )}

              {!diagnoseData.buffer_configured && (
                <div className="p-3 bg-red-50 border border-red-300 rounded text-red-900">
                  <strong>Hoof oorsaak gevind:</strong> Buffer API-sleutel is nie in stelsel ingestel nie.
                  Gaan na <em>Admin → Instellings → Sosiale Media</em> en koppel Buffer.
                </div>
              )}

              {/* Per-item table */}
              {diagnoseData.items.length === 0 ? (
                <p className="text-gray-500">Geen items in wagry nie.</p>
              ) : (
                <div className="space-y-4">
                  {diagnoseData.items.map((ad) => (
                    <div key={ad.id} className="border rounded p-3" data-testid={`diagnose-item-${ad.id}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="font-semibold">{ad.title || '(geen titel)'}</div>
                        <span className="text-xs text-gray-500">deur {ad.created_by_name || 'onbekend'} · status: {ad.status}</span>
                      </div>
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-left bg-slate-50">
                            <th className="py-1 px-2">Platform</th>
                            <th className="py-1 px-2">Status</th>
                            <th className="py-1 px-2">Teiken datum</th>
                            <th className="py-1 px-2">Binne horison?</th>
                            <th className="py-1 px-2">Laaste poging</th>
                            <th className="py-1 px-2">Laaste fout</th>
                          </tr>
                        </thead>
                        <tbody>
                          {ad.queue_plan.map((p) => (
                            <tr key={p.platform} className="border-t align-top">
                              <td className="py-1 px-2 font-semibold">{p.platform}</td>
                              <td className="py-1 px-2">
                                <span className={`px-1.5 py-0.5 rounded text-xs ${
                                  p.state === 'sent' || p.state === 'published' ? 'bg-emerald-100 text-emerald-800' :
                                  p.state === 'failed' ? 'bg-red-100 text-red-800' :
                                  p.state === 'queued' ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-700'
                                }`}>{p.state || '-'}</span>
                              </td>
                              <td className="py-1 px-2 whitespace-nowrap">
                                {p.target_date ? new Date(p.target_date).toLocaleString('af-ZA') : '-'}
                                {p.in_past && <span className="text-red-600 ml-1">(verlede)</span>}
                              </td>
                              <td className="py-1 px-2">
                                {p.in_horizon ? (
                                  <span className="text-emerald-700">Ja</span>
                                ) : (
                                  <span className="text-amber-700">
                                    Nee — nog {p.minutes_until_eligible}min
                                  </span>
                                )}
                              </td>
                              <td className="py-1 px-2 text-gray-600">
                                {p.last_attempt_at ? new Date(p.last_attempt_at).toLocaleString('af-ZA') : '-'}
                              </td>
                              <td className="py-1 px-2 text-red-700 max-w-[260px] break-words">
                                {p.last_error || '-'}
                                {!p.buffer_channel_configured && p.platform !== 'youtube' && (
                                  <div className="text-xs text-red-600 mt-1">
                                    Buffer kanaal vir {p.platform} is nie gekoppel nie
                                  </div>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={handleDiagnose} disabled={diagnoseLoading}>
              <RefreshCw className="h-4 w-4 mr-1" /> Herlaai
            </Button>
            <Button onClick={() => setDiagnoseOpen(false)}>Sluit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Adverteer;
